import { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { openWhatsAppOrder } from "../lib/whatsapp";
import { Minus, Plus, Trash2, MessageCircle, ShieldCheck } from "lucide-react";

export default function CartPage() {
  const { cart, removeItem, updateQty } = useCart();
  const { user, setLoginOpen } = useAuth();
  const [checkingOut, setCheckingOut] = useState(false);

  const checkout = () => {
    if (!user) {
      sessionStorage.setItem("cns_login_intent", "cart");
      setLoginOpen(true);
      return;
    }
    setCheckingOut(true);
    openWhatsAppOrder(cart, { name: user.name, email: user.email });
    setCheckingOut(false);
  };

  return (
    <div className="pt-24 md:pt-32 px-6 md:px-12 pb-24 min-h-screen" data-testid="cart-page">
      <div className="max-w-6xl">
        <div className="label-eyebrow mb-6">Your bag</div>
        <h1 className="font-display text-5xl md:text-7xl tracking-tighter">
          {cart.items.length === 0 ? "Empty for now." : `${cart.items.length} piece${cart.items.length > 1 ? "s" : ""}.`}
        </h1>

        {cart.items.length === 0 ? (
          <div className="mt-12">
            <Link to="/shop" className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 text-sm">
              Browse the shop
            </Link>
          </div>
        ) : (
          <div className="mt-12 grid md:grid-cols-12 gap-12">
            <div className="md:col-span-8 divide-y divide-black/10 border-y border-black/10">
              {cart.items.map((it) => (
                <div key={it.product_id} data-testid={`cart-item-${it.product_id}`} className="py-6 flex gap-6">
                  <Link to={`/shop/${it.product_id}`} className="w-24 h-24 md:w-32 md:h-32 bg-[#F7F5F0] shrink-0">
                    <img src={it.product.image_url} alt={it.product.name} className="w-full h-full object-cover" />
                  </Link>
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <Link to={`/shop/${it.product_id}`}>
                        <h3 className="font-display text-xl md:text-2xl">{it.product.name}</h3>
                      </Link>
                      <p className="label-eyebrow text-black/50 mt-1">{it.product.category}</p>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center border border-black/30 h-9">
                        <button onClick={() => updateQty(it.product_id, Math.max(1, it.quantity - 1))} className="w-9 h-full flex items-center justify-center" data-testid={`cart-dec-${it.product_id}`}>
                          <Minus size={12} />
                        </button>
                        <span className="w-9 text-center text-sm">{it.quantity}</span>
                        <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="w-9 h-full flex items-center justify-center" data-testid={`cart-inc-${it.product_id}`}>
                          <Plus size={12} />
                        </button>
                      </div>
                      <div className="font-display text-lg md:text-xl">
                        {`₹${(it.product.price * it.quantity).toFixed(2)}`}
                      </div>
                      <button onClick={() => removeItem(it.product_id)} data-testid={`cart-remove-${it.product_id}`} className="text-black/40 hover:text-black">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="md:col-span-4 space-y-6">
              <div
                className="border border-[#C9A961]/40 bg-[#F7F5F0] p-5 md:p-6"
                data-testid="whatsapp-checkout-info"
              >
                <div className="flex gap-3">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-[#C9A961]/20 flex items-center justify-center">
                    <MessageCircle size={18} className="text-[#8B7340]" />
                  </div>
                  <div>
                    <p className="font-display text-lg tracking-tight">Orders via WhatsApp</p>
                    <p className="mt-2 text-sm text-black/70 leading-relaxed">
                      We don&apos;t use online payment gateways yet. Tap below to send your bag to our
                      verified Crown &amp; Stitch number — we&apos;ll confirm stock, shipping, and secure
                      payment (UPI / bank transfer) before anything is charged.
                    </p>
                    <p className="mt-3 flex items-start gap-2 text-xs text-black/55">
                      <ShieldCheck size={14} className="shrink-0 mt-0.5 text-[#8B7340]" />
                      Your order details are sent only to us. Share your delivery address in the chat
                      if it isn&apos;t saved in your account yet.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[#F7F5F0] p-6 md:p-8 sticky top-28">
                <div className="label-eyebrow mb-4">Order summary</div>
                <div className="flex justify-between py-3 border-b border-black/10">
                  <span>Subtotal</span>
                  <span data-testid="cart-subtotal">{`₹${cart.total.toFixed(2)}`}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-black/10 text-sm text-black/60">
                  <span>Shipping</span>
                  <span>Confirmed on WhatsApp</span>
                </div>
                <div className="flex justify-between py-4 font-display text-2xl">
                  <span>Total</span>
                  <span>{`₹${cart.total.toFixed(2)}`}</span>
                </div>
                <button
                  onClick={checkout}
                  disabled={checkingOut}
                  data-testid="checkout-btn"
                  className="w-full mt-4 bg-black text-white h-12 text-sm tracking-wide hover:bg-[#C9A961] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                  {checkingOut ? "Opening WhatsApp…" : "Complete order on WhatsApp"} <MessageCircle size={14} />
                </button>
                <p className="mt-4 text-xs text-black/50 text-center">
                  Opens WhatsApp with your order pre-filled. No payment is taken until we confirm with you.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
